import React, { useCallback, useEffect } from 'react';
import {
  HStack, Box, Spinner,
} from '@gluestack-ui/themed';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { StackScreenProps } from '@react-navigation/stack';
import { useAssistantPageContextStore } from '@/admin/assistant/assistantPageContextStore';
import type { LecturerRouteParamList } from '@/common/navigation/RoleNavigations/Lecturer/types';
import type { ModuleLecturerResponseVersionHistory } from '@/common/api/models/moduleLecturerResponseVersionHistory';
import { useDialog } from '@/common/context/DialogContext';
import useUnsavedLeaveGuard from '@/common/hooks/useUnsavedLeaveGuard';
import { confirmDiscardChanges } from '@/common/utils/confirmDiscardChanges';
import Sidebar from '../Sidebar/Sidebar';
import TaskEditor from './TaskEditor/TaskEditor';
import ModuleMetaEditor from './ModuleMetaEditor/ModuleMetaEditor';
import ModulePicker from './components/ModulePicker';
import ModuleCommandStrip from './components/ModuleCommandStrip';
import EditLockBanner from './components/EditLockBanner';
import { useModuleEditorController } from './hooks/useModuleEditorController';
import { OralExamPoolJobWatchers } from './hooks/useOralExamPoolJob';

type ModuleEditorProps = StackScreenProps<LecturerRouteParamList, 'ModuleEditor'>;

/**
 * Orchestrator component for the Module Editor workspace.
 * Uses useModuleEditorController for logic and state management.
 */
const ModuleEditor: React.FC<ModuleEditorProps> = ({ navigation: screenNavigation }) => {
  const {
    module,
    moduleId,
    version,
    tasks,
    tasksQueryKey,
    isLoading,
    isModuleLoading,
    versionHistory,
    methods,
    taskId,
    hasModule,
    effectiveIsReadOnly,
    editLockReason,
    isAnyMutationPending,
    canCreateVersion,
    canDeleteVersion,
    canDeleteModule,
    canRequestApproval,
    canGenerateRubrics,
    saveMutationPending,
    createVersionMutationPending,
    deleteVersionMutationPending,
    deleteModuleMutationPending,
    requestApprovalMutationPending,
    handleSave,
    handleOpenPreview,
    handleCreateVersion,
    handleDeleteVersion,
    handleDeleteModule,
    handleRequestApproval,
    handleVersionChange,
    taskEditorRef,
    formIsDirty,
    setTaskFormIsDirty,
    setTaskSavePending,
    isTempTask,
    taskAiEnabled,
    isRubricGenerationJobActive,
    isLaunchingRubricJob,
    handleGenerateAllRubrics,
    autogradeIsReadOnly,
  } = useModuleEditorController(screenNavigation);

  const { t } = useTranslation();
  const { showDialog } = useDialog();
  const setAssistantPageContext = useAssistantPageContextStore((state) => state.setPageContext);

  useEffect(() => {
    if (!module) {
      setAssistantPageContext(null);
      return undefined;
    }

    setAssistantPageContext({
      route: 'ModuleEditor',
      moduleId: module.id,
      moduleTitle: module.title,
      version: module.version,
      workflowState: module.state?.workflowState,
      taskId: taskId || undefined,
    });

    return () => setAssistantPageContext(null);
  }, [
    module,
    module?.id,
    module?.title,
    module?.version,
    module?.state?.workflowState,
    setAssistantPageContext,
    taskId,
  ]);

  // Single screen-level guard. Covers React Navigation stack pops (back button,
  // drawer change, deep-link navigation) and -- on web -- browser refresh/close.
  // In-screen `setParams` transitions (sidebar task switch, version switch)
  // are NOT covered here; those use `confirmDiscardChanges` instead.
  useUnsavedLeaveGuard({
    isDirty: formIsDirty,
    isAutosaveInFlight: saveMutationPending,
    navigation: screenNavigation,
    showDialog,
    labels: {
      title: t('lecturer.moduleEditor.moduleEditor.dialogs.unsavedChanges.title'),
      message: t('lecturer.moduleEditor.moduleEditor.dialogs.unsavedChanges.message'),
      stay: t('common.buttons.cancel'),
      discard: t('lecturer.moduleEditor.moduleEditor.dialogs.unsavedChanges.discard'),
    },
  });

  const handleGoBack = () => {
    // We use navigate instead of goBack to ensure we always land on a valid hub
    // screen and avoid "GO_BACK not handled" errors when deep-linked or refreshed.
    // The unsaved-changes prompt is handled centrally by `useUnsavedLeaveGuard`
    // above via `usePreventRemove`, so no imperative guard is needed here.
    screenNavigation.navigate({ name: 'EvaluationDesk', params: {}, merge: false } as never);
  };

  const handleBeforeTaskSelect = useCallback((proceed: () => void) => {
    confirmDiscardChanges({
      isDirty: formIsDirty, showDialog, t, onDiscard: proceed,
    });
  }, [formIsDirty, showDialog, t]);

  // Show module picker if no module is selected
  if (!hasModule) {
    return (
      <Box flex={1} bg="$bgApp">
        <ModulePicker onBack={handleGoBack} />
      </Box>
    );
  }

  // Show loading state
  if (isModuleLoading) {
    return (
      <Box flex={1} bg="$bgApp" justifyContent="center" alignItems="center">
        <Spinner size="large" />
      </Box>
    );
  }

  const selectedTask = tasks?.find((t) => t.id === taskId);

  return (
    <Box flex={1} bg="$bgApp">
      <OralExamPoolJobWatchers
        moduleId={moduleId}
        version={version}
        tasksQueryKey={tasksQueryKey}
      />
      {/* Command Strip at top */}
      <ModuleCommandStrip
        module={module}
        versionHistory={versionHistory as ModuleLecturerResponseVersionHistory}
        selectedVersion={module?.version}
        onVersionChange={handleVersionChange}
        isLoading={isModuleLoading}
        // Actions
        onPreview={handleOpenPreview}
        onCreateVersion={handleCreateVersion}
        onDeleteVersion={handleDeleteVersion}
        onDeleteModule={handleDeleteModule}
        onRequestApproval={handleRequestApproval}
        onSave={handleSave}
        onBack={handleGoBack}
        // Show save when form is dirty (works for both module and task)
        formIsDirty={formIsDirty}
        isReadOnly={effectiveIsReadOnly}
        isAnyMutationPending={isAnyMutationPending}
        canCreateVersion={canCreateVersion}
        canDeleteVersion={canDeleteVersion}
        canDeleteModule={canDeleteModule}
        canRequestApproval={canRequestApproval}
        canGenerateRubrics={canGenerateRubrics}
        saveMutationPending={saveMutationPending}
        createVersionMutationPending={createVersionMutationPending}
        deleteVersionMutationPending={deleteVersionMutationPending}
        deleteModuleMutationPending={deleteModuleMutationPending}
        requestApprovalMutationPending={requestApprovalMutationPending}
        onGenerateAllRubrics={taskAiEnabled ? handleGenerateAllRubrics : undefined}
        isGeneratingRubrics={isRubricGenerationJobActive || isLaunchingRubricJob}
      />

      <EditLockBanner
        reason={editLockReason}
        workflowState={module?.state?.workflowState}
      />

      {/* Main content area */}
      <HStack flex={1}>
        <Sidebar
          show="tasks"
          module={module}
          submissions={[]}
          isLoading={isLoading}
          onBeforeTaskSelect={handleBeforeTaskSelect}
        />

        <Box flex={1} overflow="hidden">
          <FormProvider {...methods}>
            {!taskId ? (
              <ModuleMetaEditor
                readonly={effectiveIsReadOnly}
                autogradeIsReadOnly={autogradeIsReadOnly}
                formIsDirty={formIsDirty}
                tasks={tasks}
              />
            ) : (
              selectedTask ? (
                <TaskEditor
                  key={selectedTask.id}
                  ref={taskEditorRef}
                  task={selectedTask}
                  // TODO: TaskEditor expects expanded Module type, but we have minimal type here
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  module={module as any}
                  isReadOnly={effectiveIsReadOnly && !isTempTask}
                  onDirtyChange={setTaskFormIsDirty}
                  onSavingChange={setTaskSavePending}
                  testID="task-editor"
                />
              ) : (
                <ModuleMetaEditor
                  readonly={effectiveIsReadOnly}
                  autogradeIsReadOnly={autogradeIsReadOnly}
                  formIsDirty={formIsDirty}
                  tasks={tasks}
                />
              )
            )}
          </FormProvider>
        </Box>
      </HStack>
    </Box>
  );
};

export default ModuleEditor;
